import React, { useEffect, useState } from "react";
import {
  FiArrowRight,
  FiBarChart2,
  FiCheckCircle,
  FiChevronRight,
  FiClipboard,
  FiCreditCard,
  FiGrid,
  FiHeadphones,
  FiLayers,
  FiLogIn,
  FiPackage,
  FiShield,
  FiTrendingUp,
  FiTruck,
  FiUsers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "../../Styles/Seller/SellerHome.css";
import nexsoraimg from "../../assests/elumiya.png";
import sellerBanner from "../../assests/seller-banner.png";
import Modal from "../Modal/Modal";
import SellerRegisterForm from "./SellerRegisterForm";
import { useSellerAuth } from "../../Common/SellerAuthContext";

const heroStats = [
  { value: "250K+", label: "Verified buyer reach" },
  { value: "500+", label: "Cities supported" },
  { value: "7 Days", label: "Average payout cycle" },
];

const heroProofPoints = [
  "Simple onboarding steps",
  "Secure payment visibility",
  "Responsive seller support",
];

const heroChecklist = [
  "Seller registration and onboarding flow",
  "Catalog, order, and payout visibility",
  "A cleaner workspace for daily operations",
];

const benefits = [
  {
    icon: FiUsers,
    title: "Reach more relevant customers",
    description:
      "Present your catalog to healthcare buyers actively searching for trusted products and suppliers.",
  },
  {
    icon: FiLayers,
    title: "List and manage products with clarity",
    description:
      "Keep pricing, specifications, and catalog structure organized in a workflow designed for sellers.",
  },
  {
    icon: FiTrendingUp,
    title: "Grow with better visibility",
    description:
      "Use marketplace insights and seller tools to understand demand, momentum, and next best actions.",
  },
  {
    icon: FiCreditCard,
    title: "Track payments with confidence",
    description:
      "See settlements, invoices, and payout-related information in a more business-friendly experience.",
  },
  {
    icon: FiTruck,
    title: "Support fulfilment and delivery",
    description:
      "Coordinate order movement with logistics visibility that helps your team stay on schedule.",
  },
  {
    icon: FiHeadphones,
    title: "Access seller support when needed",
    description:
      "Get help with onboarding, account activity, and operational questions as your business scales.",
  },
];

const onboardingSteps = [
  {
    step: "01",
    icon: FiLogIn,
    title: "Register your seller account",
    description:
      "Start with the existing registration flow using your business and contact information.",
  },
  {
    step: "02",
    icon: FiClipboard,
    title: "Submit your business details",
    description:
      "Complete the current onboarding requirements so your account can move through activation.",
  },
  {
    step: "03",
    icon: FiPackage,
    title: "Add products and pricing",
    description:
      "Build your catalog with listing details, inventory information, and product specifications.",
  },
  {
    step: "04",
    icon: FiBarChart2,
    title: "Manage orders and start growing",
    description:
      "Use the seller dashboard to monitor orders, fulfilment, and payouts once you are live.",
  },
];

const trustPillars = [
  {
    icon: FiShield,
    title: "Trust-building seller presence",
    description:
      "Show buyers a professional marketplace experience that feels reliable from discovery to fulfilment.",
  },
  {
    icon: FiCreditCard,
    title: "Transparent payment experience",
    description:
      "Keep settlement timing, invoice visibility, and financial follow-up easier to understand.",
  },
  {
    icon: FiTruck,
    title: "Operational logistics support",
    description:
      "Give your team clearer delivery coordination and order movement visibility where it matters most.",
  },
  {
    icon: FiGrid,
    title: "Seller dashboard visibility",
    description:
      "Track products, orders, and performance from a workspace designed to support daily seller activity.",
  },
];

const showcaseHighlights = [
  {
    title: "Organize your catalog faster",
    description:
      "Keep product information, categories, and pricing structured for smoother day-to-day management.",
  },
  {
    title: "Stay on top of incoming orders",
    description:
      "See what needs action next and reduce the friction of juggling multiple seller workflows.",
  },
  {
    title: "Use insights to guide growth",
    description:
      "Follow performance trends and demand patterns to make smarter commercial decisions.",
  },
];

const workspaceRows = [
  {
    title: "Catalog",
    items: ["Listings", "Pricing", "Categories"],
  },
  {
    title: "Orders",
    items: ["Incoming", "Processing", "Delivered"],
  },
  {
    title: "Payments",
    items: ["Invoices", "Settlements", "Reports"],
  },
];

export default function SellerHome() {
  const [openRegister, setOpenRegister] = useState(false);
  const navigate = useNavigate();
  const { isSellerAuthenticated } = useSellerAuth();

  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll(".seller-home-reveal"),
    );

    if (!elements.length) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      elements.forEach((element) => element.classList.add("active"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.12 },
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      elements.forEach((element) => observer.unobserve(element));
      observer.disconnect();
    };
  }, []);

  const href = isSellerAuthenticated ? "/dashboard" : "/sellerlogin";
  const navLabel = isSellerAuthenticated ? "Open Dashboard" : "Seller Login";

  return (
    <>
      <div className="seller-home-page">
        <header className="seller-home-hero" role="banner">
          <div className="seller-home-shell">
            <div className="seller-home-nav">
              <a
                href="/"
                className="seller-home-logo-link"
                aria-label="Elumia Med Store seller home"
              >
                <img
                  src={nexsoraimg}
                  alt="Elumia Med Store"
                  className="seller-home-logo"
                />
                {/* <div className="seller-home-logo-copy">
                  <strong>Elumia Med Store</strong>
                  <span>Seller marketplace</span>
                </div> */}
              </a>

              <button
                type="button"
                className="seller-home-nav-action"
                onClick={() => navigate(href)}
              >
                <FiLogIn aria-hidden="true" />
                {navLabel}
              </button>
            </div>
          </div>

          <div className="seller-home-banner-spotlight seller-home-reveal">
            <button
              type="button"
              className="seller-home-banner-button"
              onClick={() => setOpenRegister(true)}
              aria-label="Open seller registration from featured seller banner"
            >
              <img
                src={sellerBanner}
                alt="Start selling on ELUMIA and grow your medical equipment business"
                className="seller-home-banner-image"
              />
              {/* <span className="seller-home-banner-ribbon">
                Become a Seller
                <FiArrowRight aria-hidden="true" />
              </span> */}
            </button>
          </div>

          <div className="seller-home-shell">
            <div className="seller-home-hero__grid">
              <div className="seller-home-hero__copy seller-home-reveal">
                <span className="seller-home-eyebrow">
                  Seller growth, simplified
                </span>

                <h1 className="seller-home-title">
                  Build your healthcare business on a marketplace sellers can trust.
                </h1>

                <p className="seller-home-description">
                  Reach more buyers, complete onboarding with clarity, and manage
                  products, orders, and payouts from one seller workspace.
                </p>

                <div className="seller-home-actions">
                  <button
                    type="button"
                    className="seller-home-btn seller-home-btn--primary"
                    onClick={() => setOpenRegister(true)}
                  >
                    Start Selling
                    <FiArrowRight aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    className="seller-home-btn seller-home-btn--secondary"
                    onClick={() => navigate(href)}
                  >
                    Seller Login
                  </button>

                  <a
                    className="seller-home-btn seller-home-btn--ghost"
                    href="#seller-benefits"
                  >
                    Learn More
                  </a>
                </div>

                <ul className="seller-home-proof-list">
                  {heroProofPoints.map((item) => (
                    <li key={item} className="seller-home-proof-item">
                      <FiCheckCircle aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="seller-home-hero__panel seller-home-reveal">
                <article className="seller-home-panel-card seller-home-panel-card--stats">
                  <div className="seller-home-card-heading">
                    <span className="seller-home-card-kicker">
                      Seller value proposition
                    </span>
                    <h2>Why vendors start here</h2>
                  </div>

                  <div className="seller-home-stat-grid">
                    {heroStats.map((item) => (
                      <div className="seller-home-stat-card" key={item.label}>
                        <strong>{item.value}</strong>
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </article>

                <div className="seller-home-panel-grid">
                  <article className="seller-home-panel-card">
                    <span className="seller-home-card-kicker">
                      Operational support
                    </span>
                    <ul className="seller-home-checklist">
                      {heroChecklist.map((item) => (
                        <li key={item}>
                          <FiCheckCircle aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </article>

                  <article className="seller-home-panel-card seller-home-panel-card--accent">
                    <span className="seller-home-card-kicker">
                      Built for daily seller work
                    </span>
                    <h3>
                      Catalog, orders, payouts, and performance in one cleaner flow.
                    </h3>
                    <p>
                      Give your team a seller experience that feels structured,
                      premium, and easier to act on.
                    </p>
                    <div className="seller-home-pill-row">
                      <span className="seller-home-pill">Product listings</span>
                      <span className="seller-home-pill">Order flow</span>
                      <span className="seller-home-pill">Seller support</span>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="seller-home-main">
          <section
            id="seller-benefits"
            className="seller-home-section seller-home-section--surface seller-home-reveal"
          >
            <div className="seller-home-shell">
              <div className="seller-home-section-head">
                <span className="seller-home-kicker">Why sell with us</span>
                <h2 className="seller-home-section-title">
                  A seller landing experience built around confidence and
                  clarity.
                </h2>
                <p className="seller-home-section-copy">
                  The goal is simple: make the marketplace easier for vendors to
                  understand, easier to trust, and easier to act on.
                </p>
              </div>

              <div className="seller-home-benefit-grid">
                {benefits.map(({ icon: Icon, title, description }) => (
                  <article className="seller-home-benefit-card" key={title}>
                    <div className="seller-home-icon-box">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="seller-home-section seller-home-reveal">
            <div className="seller-home-shell">
              <div className="seller-home-section-head seller-home-section-head--left">
                <span className="seller-home-kicker">How it works</span>
                <h2 className="seller-home-section-title">
                  A clearer path from registration to your first orders.
                </h2>
                <p className="seller-home-section-copy">
                  Sellers should know exactly what comes next. This step-by-step
                  flow keeps onboarding easy to scan without changing the actual
                  process behind it.
                </p>
              </div>

              <div className="seller-home-step-grid">
                {onboardingSteps.map(
                  ({ step, icon: Icon, title, description }) => (
                    <article className="seller-home-step-card" key={step}>
                      <div className="seller-home-step-top">
                        <span className="seller-home-step-number">{step}</span>
                        <div className="seller-home-step-icon">
                          <Icon aria-hidden="true" />
                        </div>
                      </div>
                      <h3>{title}</h3>
                      <p>{description}</p>
                    </article>
                  ),
                )}
              </div>
            </div>
          </section>

          <section className="seller-home-section seller-home-section--dark seller-home-reveal">
            <div className="seller-home-shell">
              <div className="seller-home-section-head seller-home-section-head--light">
                <span className="seller-home-kicker">Trust and support</span>
                <h2 className="seller-home-section-title">
                  Sellers need more than traffic. They need operational
                  confidence.
                </h2>
                <p className="seller-home-section-copy">
                  From payment visibility to seller assistance and order
                  coordination, the page now explains the business support layer
                  much more clearly.
                </p>
              </div>

              <div className="seller-home-trust-grid">
                {trustPillars.map(({ icon: Icon, title, description }) => (
                  <article className="seller-home-trust-card" key={title}>
                    <div className="seller-home-icon-box seller-home-icon-box--dark">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{title}</h3>
                    <p>{description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="seller-home-section seller-home-section--soft seller-home-reveal">
            <div className="seller-home-shell seller-home-showcase">
              <div className="seller-home-showcase__copy">
                <div className="seller-home-section-head seller-home-section-head--left seller-home-section-head--compact">
                  <span className="seller-home-kicker">Seller workspace</span>
                  <h2 className="seller-home-section-title">
                    A cleaner way to understand what your team needs to do next.
                  </h2>
                  <p className="seller-home-section-copy">
                    The seller experience should make everyday work feel more
                    visible and more manageable, from listings to settlements.
                  </p>
                </div>

                <div className="seller-home-highlight-list">
                  {showcaseHighlights.map((item) => (
                    <article
                      className="seller-home-highlight-card"
                      key={item.title}
                    >
                      <div className="seller-home-highlight-icon">
                        <FiChevronRight aria-hidden="true" />
                      </div>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <div className="seller-home-dashboard-card">
                <div className="seller-home-dashboard-card__header">
                  <div>
                    <span className="seller-home-card-kicker">
                      Marketplace control center
                    </span>
                    <h3>Seller dashboard preview</h3>
                  </div>
                  <span className="seller-home-status-pill">
                    Live visibility
                  </span>
                </div>

                <div className="seller-home-dashboard-metrics">
                  <div className="seller-home-dashboard-metric">
                    <strong>Orders</strong>
                    <span>Track incoming demand</span>
                  </div>
                  <div className="seller-home-dashboard-metric">
                    <strong>Payouts</strong>
                    <span>Follow settlement progress</span>
                  </div>
                  <div className="seller-home-dashboard-metric">
                    <strong>Analytics</strong>
                    <span>Spot growth opportunities</span>
                  </div>
                </div>

                <div className="seller-home-dashboard-rows">
                  {workspaceRows.map((row) => (
                    <div className="seller-home-dashboard-row" key={row.title}>
                      <div className="seller-home-dashboard-row__title">
                        {row.title}
                      </div>
                      <div className="seller-home-dashboard-row__tags">
                        {row.items.map((item) => (
                          <span
                            className="seller-home-dashboard-tag"
                            key={item}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="seller-home-section seller-home-section--cta seller-home-reveal">
            <div className="seller-home-shell">
              <div className="seller-home-cta-card">
                <div className="seller-home-cta-copy">
                  <span className="seller-home-kicker">
                    Start selling with confidence
                  </span>
                  <h2 className="seller-home-section-title">
                    Bring your catalog to more buyers without adding onboarding
                    confusion.
                  </h2>
                  <p className="seller-home-section-copy">
                    Register, complete the existing seller onboarding flow, and
                    move into product setup and order management with a clearer
                    first experience.
                  </p>
                </div>

                <div className="seller-home-cta-actions">
                  <button
                    type="button"
                    className="seller-home-btn seller-home-btn--primary"
                    onClick={() => setOpenRegister(true)}
                  >
                    Become a Seller
                  </button>

                  <button
                    type="button"
                    className="seller-home-btn seller-home-btn--secondary seller-home-btn--secondary-light"
                    onClick={() => navigate(href)}
                  >
                    Login to Seller Account
                  </button>

                  <p className="seller-home-cta-note">
                    Clear onboarding. Better business messaging. The same
                    working seller flow behind the scenes.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      <Modal
        isOpen={openRegister}
        onClose={() => setOpenRegister(false)}
        maxWidth="900px"
      >
        <SellerRegisterForm  onClose={() => setOpenRegister(false)} />
      </Modal>
    </>
  );
}
